import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Select,
    MenuItem,
    Tooltip,
    Snackbar,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    Paper,
    // ✅✅✅ FIX #1 (a): useTheme + alpha import kiya — dark mode detect karne ke liye
    useTheme,
    alpha,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import SaveIcon from "@mui/icons-material/Save";
import { UpdateMOSPhaseApi } from "../../../Services/Services";

const STATUS_OPTIONS = ["Pending", "In Progress", "Completed"];

const STATUS_COLOR = {
    Completed: "#2e7d32",
    "In Progress": "#ed6c02",
    Pending: "#9e9e9e",
};

// ✅✅✅ FIX #1 (b): Dark mode ke liye HALKHE (brighter) status colors
// Light mode wale hex (#2e7d32 green, #ed6c02 orange) dark background pe
// bahut dark padte hain — text padha nahi jaata. Light mode SAME rahega.
const STATUS_COLOR_DARK = {
    Completed: "#66bb6a",
    "In Progress": "#ffa726",
    Pending: "#b5b5b5",
};

const STATUS_LABEL = {
    Completed: "Done",
    "In Progress": "WIP",
    Pending: "Pending",
};

const initials = (name = "") =>
    name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const MOSPhaseModal = ({ open, onClose, mos, onPhaseUpdated }) => {
    const [savingId, setSavingId] = useState(null);
    const [pendingChanges, setPendingChanges] = useState({});
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

    // ✅✅✅ FIX #1 (c): theme hooks + saare theme-aware colors ek jagah
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Status color mode ke hisaab se chuno (light pe bilkul purane hex)
    const getStatusColor = (status) =>
        (isDark ? STATUS_COLOR_DARK : STATUS_COLOR)[status] ||
        (isDark ? STATUS_COLOR_DARK.Pending : STATUS_COLOR.Pending);

    // ✅✅✅ FIX #4 helper: Sticky TableHead ka SOLID theme-aware color
    // Pehle: "grey.100" hardcode tha → dark mode me BHI safed lagta tha
    // Light mode SAME: grey.100 ka hi hex hai (#f5f5f5)
    const tableHeadBg = isDark ? "#252b38" : "#f5f5f5";

    // ✅✅✅ FIX #3 helper: DialogContent ka background
    // Pehle: "grey.50" hardcode → dark me table ke peeche light grey dikh raha tha
    // Light mode SAME grey.50
    const contentBg = isDark ? "#1a1d26" : "#fafafa";

    // ✅✅✅ FIX #5 helper: Dirty (edited) row ka highlight
    // Pehle: warning.lighter / warning.light — dark theme me dhang se nahi dikhta
    // Rows sticky nahi hain, isliye alpha() yaha allowed hai (peeche contentBg solid hai)
    // Light mode SAME: warning.lighter / warning.light
    const dirtyRowBg = isDark
        ? alpha(theme.palette.warning.main, 0.18)
        : "warning.lighter";
    const dirtyRowHoverBg = isDark
        ? alpha(theme.palette.warning.main, 0.28)
        : "warning.light";

    if (!mos) return null;

    const phases = [...(mos.mos_phases || [])].sort((a, b) => a.order_no - b.order_no);
    const completedCount = phases.filter((p) => p.status === "Completed").length;
    const progress = phases.length ? Math.round((completedCount / phases.length) * 100) : 0;

    const handleSelectChange = (phaseId, newStatus) => {
        setPendingChanges((prev) => ({ ...prev, [phaseId]: newStatus }));
    };

    const handleDiscard = (phaseId) => {
        setPendingChanges((prev) => {
            const next = { ...prev };
            delete next[phaseId];
            return next;
        });
    };

    const handleSave = async (phase) => {
        const newStatus = pendingChanges[phase.id];
        if (!newStatus || newStatus === phase.status) return;

        try {
            setSavingId(phase.id);
            const response = await UpdateMOSPhaseApi(phase.id, { status: newStatus });
            const updatedPhase = response.data;

            onPhaseUpdated?.(phase.id, updatedPhase);

            setPendingChanges((prev) => {
                const next = { ...prev };
                delete next[phase.id];
                return next;
            });

            setToast({ open: true, message: response.message || `${phase.phase_name} updated`, severity: "success" });
        } catch (error) {
            console.error("Failed to update phase:", error);
            const apiMessage = error?.response?.data?.message || error?.response?.data?.errors || "Something went wrong";
            setToast({
                open: true,
                message: `Failed to update ${phase.phase_name}: ${typeof apiMessage === "string" ? apiMessage : "check fields"}`,
                severity: "error",
            });
        } finally {
            setSavingId(null);
        }
    };

    // ——— Roadmap data ———
    const totalPhases = phases.length;
    const roadmapPhases = phases.slice(0, 12); // show max 12 on roadmap

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: "hidden",
                    // ✅✅✅ FIX #2: Dialog paper dark mode me theme-aware
                    // (backgroundImage: "none" — dark elevation ka white overlay hatata hai)
                    bgcolor: "background.paper",
                    backgroundImage: "none",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: isDark
                        ? "0 20px 60px rgba(0,0,0,0.65)"
                        : "0 20px 60px rgba(0,0,0,0.15)",
                }
            }}
        >
            {/* ——— HEADER ——— */}
            <DialogTitle
                sx={{
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6" fontWeight={700} letterSpacing={-0.3}>
                            MOS Phase Progress
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {mos.so_no || "MOS"}
                            {mos.project_name && (
                                <>
                                    <Box component="span" sx={{ color: "divider", mx: 0.5 }}>•</Box>
                                    {mos.project_name}
                                </>
                            )}
                            <Box component="span" sx={{ color: "divider", mx: 0.5 }}>•</Box>
                            <Chip
                                label={`${progress}%`}
                                size="small"
                                sx={{
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: progress === 100 ? "success.main" : "primary.main",
                                    color: "#fff",
                                    borderRadius: 1,
                                    px: 0.5,
                                }}
                            />
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                            {completedCount}/{totalPhases} done
                        </Typography>
                        <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* ——— ROADMAP ——— */}
                <Box sx={{ mt: 2, position: "relative", overflowX: "auto", pb: 0.5 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0,
                            minWidth: roadmapPhases.length * 72,
                            position: "relative",
                            px: 0.5,
                        }}
                    >
                        {roadmapPhases.map((phase, idx) => {
                            const effectiveStatus = pendingChanges[phase.id] || phase.status;
                            // ✅ FIX #1: status color ab mode ke hisaab se (dark me bright)
                            const color = getStatusColor(effectiveStatus);
                            const isCompleted = effectiveStatus === "Completed";
                            const isInProgress = effectiveStatus === "In Progress";
                            const isPending = effectiveStatus === "Pending";
                            const isLast = idx === roadmapPhases.length - 1;

                            return (
                                <Box
                                    key={phase.id}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        flex: 1,
                                        minWidth: 64,
                                        position: "relative",
                                    }}
                                >
                                    {/* Connector line */}
                                    {!isLast && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 13,
                                                left: "50%",
                                                right: "-50%",
                                                height: 2.5,
                                                bgcolor: isCompleted ? color : "divider",
                                                opacity: isPending ? 0.25 : isInProgress ? 0.6 : 1,
                                                transition: "all 0.4s ease",
                                                borderRadius: 2,
                                                zIndex: 0,
                                            }}
                                        />
                                    )}

                                    {/* Dot */}
                                    <Tooltip title={`${phase.phase_name} — ${effectiveStatus}`} arrow placement="top">
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: isPending ? "transparent" : color,
                                                border: `2.5px solid ${color}`,
                                                // ✅ FIX #1: dark me dot ka bg bright ho gaya, isliye
                                                // text ko dark rakhna padega (contrast ke liye)
                                                color: isPending
                                                    ? color
                                                    : isDark
                                                    ? "#14171f"
                                                    : "#fff",
                                                fontSize: 11,
                                                fontWeight: 700,
                                                transition: "all 0.3s ease",
                                                cursor: "pointer",
                                                position: "relative",
                                                zIndex: 1,
                                                boxShadow: isCompleted ? `0 0 0 4px ${color}33` : "none",
                                                "&:hover": {
                                                    transform: "scale(1.12)",
                                                    boxShadow: `0 0 0 6px ${color}22`,
                                                },
                                            }}
                                        >
                                            {isCompleted ? (
                                                <CheckIcon sx={{ fontSize: 14 }} />
                                            ) : isInProgress ? (
                                                // ✅ FIX #1: In-Progress ka inner dot bhi contrast ke saath
                                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: isDark ? "#14171f" : "#fff" }} />
                                            ) : (
                                                phase.order_no
                                            )}
                                        </Box>
                                    </Tooltip>

                                    {/* Label */}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            mt: 0.6,
                                            fontSize: 9,
                                            fontWeight: 600,
                                            color: isCompleted ? "text.primary" : "text.disabled",
                                            textAlign: "center",
                                            lineHeight: 1.2,
                                            maxWidth: 56,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            transition: "color 0.3s ease",
                                        }}
                                    >
                                        {phase.phase_name.length > 8
                                            ? phase.phase_name.slice(0, 7) + "…"
                                            : phase.phase_name}
                                    </Typography>

                                    {/* Small status badge under label */}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: 7,
                                            fontWeight: 600,
                                            color: color,
                                            opacity: isPending ? 0.4 : 0.8,
                                            letterSpacing: 0.3,
                                            textTransform: "uppercase",
                                            mt: 0.2,
                                        }}
                                    >
                                        {STATUS_LABEL[effectiveStatus] || effectiveStatus}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Progress summary bar */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5 }}>
                    <Box sx={{ flex: 1, height: 4, borderRadius: 4, bgcolor: "action.hover", overflow: "hidden" }}>
                        <Box
                            sx={{
                                width: `${progress}%`,
                                height: "100%",
                                borderRadius: 4,
                                bgcolor: progress === 100 ? "success.main" : "primary.main",
                                transition: "width 0.6s ease",
                            }}
                        />
                    </Box>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ minWidth: 36, textAlign: "right" }}>
                        {progress}%
                    </Typography>
                </Box>
            </DialogTitle>

            {/* ——— TABLE ——— */}
            {/* ✅✅✅ FIX #3: contentBg — pehle "grey.50" hardcoded tha (dark me white!) */}
            <DialogContent sx={{ p: 0, bgcolor: contentBg }}>
                <TableContainer
                    sx={{
                        maxHeight: 460,
                        "&::-webkit-scrollbar": { width: 6, height: 6 },
                        "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                        "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 6 },
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            {/* ✅✅✅ FIX #4: Sticky header SOLID theme-aware
                                Pehle: "grey.100" hardcoded — dark me sticky header ke
                                NEECHE scroll hoti rows unhe paar karke dikhti thi (transparency)
                                Sticky = SOLID color (alpha nahi), light SAME #f5f5f5 */}
                            <TableRow sx={{ bgcolor: tableHeadBg }}>
                                <TableCell sx={{ fontWeight: 700, width: 36, py: 1.2, bgcolor: tableHeadBg, color: "text.primary" }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: tableHeadBg, color: "text.primary" }}>Phase</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 170, bgcolor: tableHeadBg, color: "text.primary" }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 60, bgcolor: tableHeadBg, textAlign: "center", color: "text.primary" }}>Days</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 150, bgcolor: tableHeadBg, color: "text.primary" }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 56, bgcolor: tableHeadBg, textAlign: "center", color: "text.primary" }}>Save</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {phases.map((phase) => {
                                const pending = pendingChanges[phase.id];
                                const isDirty = Boolean(pending && pending !== phase.status);
                                const effectiveStatus = pending || phase.status;
                                // ✅ FIX #1: status color ab mode ke hisaab se
                                const color = getStatusColor(effectiveStatus);
                                const isSaving = savingId === phase.id;
                                const showUpdatedBy = phase.status === "Completed" && phase.completed_by_name;

                                return (
                                    <TableRow
                                        key={phase.id}
                                        sx={{
                                            // ✅ FIX #5: dirty row highlight dark-friendly
                                            bgcolor: isDirty ? dirtyRowBg : "transparent",
                                            transition: "background 0.2s ease",
                                            "&:hover": {
                                                bgcolor: isDirty ? dirtyRowHoverBg : "action.hover",
                                            },
                                            "& td": { py: 0.8, borderBottom: "1px solid", borderColor: "divider" },
                                        }}
                                    >
                                        {/* # */}
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    // ✅ FIX #1: dark me bright dot → dark text (contrast)
                                                    color: effectiveStatus === "Pending"
                                                        ? color
                                                        : isDark
                                                        ? "#14171f"
                                                        : "#fff",
                                                    bgcolor: effectiveStatus === "Pending" ? "transparent" : color,
                                                    border: `2px solid ${color}`,
                                                    transition: "all 0.3s ease",
                                                }}
                                            >
                                                {effectiveStatus === "Completed" ? (
                                                    <CheckIcon sx={{ fontSize: 13 }} />
                                                ) : (
                                                    phase.order_no
                                                )}
                                            </Box>
                                        </TableCell>

                                        {/* Phase Name */}
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} noWrap>
                                                {phase.phase_name}
                                            </Typography>
                                        </TableCell>

                                        {/* Updated By */}
                                        <TableCell>
                                            {showUpdatedBy ? (
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Tooltip title={phase.completed_by_name}>
                                                        <Avatar
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                fontSize: 10,
                                                                bgcolor: "primary.main",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {initials(phase.completed_by_name)}
                                                        </Avatar>
                                                    </Tooltip>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="caption" noWrap sx={{ display: "block", fontWeight: 500 }}>
                                                            {phase.completed_by_name}
                                                        </Typography>
                                                        {phase.updated_at && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.disabled"
                                                                sx={{ fontSize: 9, display: "block" }}
                                                            >
                                                                {new Date(phase.updated_at).toLocaleDateString(undefined, {
                                                                    month: "short",
                                                                    day: "numeric",
                                                                    year: "numeric",
                                                                })}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* Days */}
                                        <TableCell align="center">
                                            <Typography variant="body2" fontWeight={500} color="text.secondary">
                                                {phase.days ?? "—"}
                                            </Typography>
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell>
                                            <Select
                                                value={effectiveStatus}
                                                disabled={isSaving}
                                                onChange={(e) => handleSelectChange(phase.id, e.target.value)}
                                                variant="standard"
                                                disableUnderline
                                                fullWidth
                                                sx={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    // ✅ FIX #1: dark me bright status color — ab readable
                                                    color,
                                                    bgcolor: `${color}0f`,
                                                    borderRadius: 1.5,
                                                    px: 1,
                                                    py: 0.2,
                                                    "& .MuiSelect-select": { py: 0.4, pl: 0.5 },
                                                    "& .MuiSelect-icon": { color },
                                                    "&:hover": {
                                                        bgcolor: `${color}20`,
                                                    },
                                                }}
                                            >
                                                {STATUS_OPTIONS.map((status) => (
                                                    <MenuItem key={status} value={status} sx={{ fontSize: 12, fontWeight: 500 }}>
                                                        {status}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>

                                        {/* Save / Actions */}
                                        <TableCell align="center">
                                            {isSaving ? (
                                                <CircularProgress size={18} sx={{ color: "primary.main" }} />
                                            ) : isDirty ? (
                                                <Box display="flex" gap={0.2} justifyContent="center">
                                                    <Tooltip title="Save changes">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleSave(phase)}
                                                            sx={{
                                                                p: 0.4,
                                                                bgcolor: "primary.lighter",
                                                                "&:hover": { bgcolor: "primary.light" },
                                                            }}
                                                        >
                                                            <SaveIcon sx={{ fontSize: 17 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Discard">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDiscard(phase.id)}
                                                            sx={{
                                                                p: 0.4,
                                                                color: "error.main",
                                                                "&:hover": { bgcolor: "error.lighter" },
                                                            }}
                                                        >
                                                            <CloseIcon sx={{ fontSize: 17 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                                                    ✓
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Footer note if many phases */}
                {phases.length > 8 && (
                    <Box
                        sx={{
                            p: 1.5,
                            textAlign: "center",
                            borderTop: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            Showing {phases.length} phases  ·  Scroll for more
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            {/* ——— TOAST ——— */}
            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={toast.severity}
                    variant="filled"
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    sx={{ borderRadius: 2, fontWeight: 500 }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default MOSPhaseModal;
